import {
  Currencies,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery,
  NanoIDFilter
} from '../../../../types';
import {
  FetchGeneralLedgersCacheKey,
  FetchCacheGeneralLedgersFilters,
  FetchGeneralLedgersSort,
  FetchGeneralLedgersPage,
  FetchGeneralLedgersLimit,
  FetchGeneralLedgersGqlQuery
} from '../../generalLedgersTypes';
import { FetchCacheGeneralLedgersQueryResponse } from '../../queries/fetchCacheGeneralLedgers.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_GENERAL_LEDGERS_FILTERS,
  INITIAL_GENERAL_LEDGERS_LIMIT,
  INITIAL_GENERAL_LEDGERS_PAGE,
  INITIAL_GENERAL_LEDGERS_SORT
} from '../../generalLedgersConstants';

interface GeneralLedgersWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface GeneralLedgersWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface GeneralLedgersDefaultOptions {
  action?: string;
  cacheKey: FetchGeneralLedgersCacheKey;
  query: FetchGeneralLedgersGqlQuery;
  initialFilters?: FetchCacheGeneralLedgersFilters;
  initialSort?: FetchGeneralLedgersSort;
  initialPage?: FetchGeneralLedgersPage;
  initialLimit?: FetchGeneralLedgersLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
  additionalParams?: {
    convertTo?: Currencies;
    itemsAvaitedFilters?: {
      projectNanoId?: NanoIDFilter;
      taskNanoId?: NanoIDFilter;
    };
  };
}

type GeneralLedgersOptions = GeneralLedgersDefaultOptions &
  (
    | GeneralLedgersWithPrefetchItemOptions
    | GeneralLedgersWithoutPrefetchItemOptions
  );

const scope = 'generalLedgers';

function useCachePaginatedGeneralLedgers<
  FetchGeneralLedgersGeneralLedgerType = FetchCacheGeneralLedgersQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_GENERAL_LEDGERS_FILTERS,
  initialSort = INITIAL_GENERAL_LEDGERS_SORT,
  initialPage = INITIAL_GENERAL_LEDGERS_PAGE,
  initialLimit = INITIAL_GENERAL_LEDGERS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {},
  additionalParams = {}
}: GeneralLedgersOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchGeneralLedgersGeneralLedgerType>({
    action,
    additionalParams,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    generalLedgersData: data,
    generalLedgers: items,
    generalLedgersError: itemsError,
    generalLedgersErrorMessage: itemsErrorMessage,
    generalLedgersTotalCount: itemsTotalCount,
    generalLedgersFetched: isFetched,
    generalLedgersLoading: isLoading,
    generalLedgersIsPlaceholderData: isPlaceholderData,
    generalLedgersFilters: currentFilters,
    generalLedgersSort: currentSort,
    generalLedgersPage: currentPage,
    generalLedgersLimit: currentLimit,
    updateGeneralLedgerCache: updateItemCache,
    updateGeneralLedgersCache: updateItemsCache,
    filterGeneralLedgers: filterItems,
    changeGeneralLedgersFilters: changeItemsFilters,
    clearGeneralLedgersFilters: clearItemsFilters,
    sortGeneralLedgers: sortItems,
    paginateGeneralLedgers: paginateItems,
    limitGeneralLedgers: limitItems,
    prefetchGeneralLedgers: prefetchItems,
    prefetchGeneralLedger: prefetchItem
  };
}

export default useCachePaginatedGeneralLedgers;
